import CreditMemoIcon from '@assets/icons/credit-memo.svg';
import i18n from '@/utils/i18n';
import InvoiceIcon from '@assets/icons/invoice.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { ListOption } from '@/components/list-item';
import { SCREEN_PADDING, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';

export const PAYMENT_LINK_DETAIL_LIST_ITEM_HEIGHT = 64;

export enum PaymentLinkDetailListItemType {
  CREDIT_MEMO = 'CREDIT_MEMO',
  INVOICE = 'INVOICE',
}

export interface PaymentLinkDetailListItemFields {
  amount_in_micros: number;
  date: string;
  number: string;
  type: PaymentLinkDetailListItemType;
  uuid: string;
}

export type PaymentLinkDetailItemOption = ListOption<{
  paymentLinkLineItem: PaymentLinkDetailListItemFields;
}>;

interface Props {
  currencyCode: string | null;
  option: PaymentLinkDetailItemOption;
  setSelectedListItem: (
    paymentLinkLineItem: PaymentLinkDetailListItemFields,
  ) => void;
}

export const PaymentLinkDetailListItem: React.FC<Props> = ({
  currencyCode,
  option,
  setSelectedListItem,
}) => {
  const [styles, theme] = useStyles();

  const baseStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(styles.base, [
    styles.base,
    styles.baseLarge,
  ]);

  const { paymentLinkLineItem } = option.metadata!;

  return (
    <TouchableOpacity
      onPress={() => setSelectedListItem(paymentLinkLineItem)}
      style={baseStyle}
    >
      <View style={styles.textWithIcon}>
        <View style={styles.iconContainer}>
          {paymentLinkLineItem.type ===
          PaymentLinkDetailListItemType.CREDIT_MEMO ? (
            <CreditMemoIcon stroke={theme.sk.color.icon.primary} />
          ) : (
            <InvoiceIcon stroke={theme.sk.color.icon.primary} />
          )}
        </View>
        <View style={styles.textDetailsContainer}>
          <Text style={[TYPOGRAPHY.h3, styles.text]}>
            {paymentLinkLineItem.number}
          </Text>
          <Text style={[TYPOGRAPHY.h4, styles.dateText]}>
            {paymentLinkLineItem.type ===
            PaymentLinkDetailListItemType.CREDIT_MEMO
              ? i18n.t('issued_date', {
                  date: paymentLinkLineItem.date,
                })
              : i18n.t('due_date', {
                  date: paymentLinkLineItem.date,
                })}
          </Text>
        </View>
      </View>
      <View style={styles.amountContainer}>
        <Text style={[TYPOGRAPHY.h4, styles.amountText]}>
          {formatCurrency(paymentLinkLineItem.amount_in_micros, currencyCode)}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  amountContainer: {
    height: PAYMENT_LINK_DETAIL_LIST_ITEM_HEIGHT,
    justifyContent: 'flex-start',
    paddingTop: 14,
  },
  amountText: {
    fontSize: 16,
    lineHeight: 16,
  },
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    height: PAYMENT_LINK_DETAIL_LIST_ITEM_HEIGHT,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
  },
  baseLarge: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  dateText: {
    color: theme.sk.color.text.secondary,
    fontSize: 14,
    lineHeight: 21,
  },
  iconContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
  },
  textDetailsContainer: {
    flexDirection: 'column',
  },
  textWithIcon: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
  },
}));
